import React, { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { NetworkStatus } from '@apollo/client';
import { Box, Flex } from '@indeed/ifl-components';
import { PastAnswersContent, PaginationControls } from './PastAnswers';
import { ErrorDialog } from '../../../components/ErrorDialog/ErrorDialog';
import {
    useGetAutoApplyEligiblePastAnswersQuery,
    useGetSqPrefillPreferenceQuery,
} from './__generated__/PastAnswers.generated';
import GettextFactory from '../../../util/GettextFactory';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { StandalonePageHeader } from '../components/StandalonePageHeader/StandalonePageHeader';
import { useSearchParams } from 'react-router-dom';
import { useSqPrefillPreference } from '../hooks/useSqPrefillPreference';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';

const ITEMS_PER_PAGE = 10;

export const useShouldIncludeHostedSQ = (): boolean => {
    const { getGroup } = useProctorGroups();
    const value = getGroup('ia_afm_iqv2_prefill_tst')?.value;
    return value === 1 || value === 2;
};

/**
 * Past Answers Page for Auto Apply Setup
 * Displays past screener questions in a full page view with pagination.
 * Fetches one page at a time via Relay cursor pagination; uses totalCount
 * from the API response to drive pagination controls.
 */
const PastAnswersPage: React.FC = () => {
    const gt = GettextFactory();
    const { locale, returnUrl } = useInitialState();
    const copy = useApplyForMeCopy();

    const [showErrorDialog, setShowErrorDialog] = useState(false);
    // Stores the retry action for "Try again" button, varies by error type
    const [retryAction, setRetryAction] = useState<() => void>(() => () => {});

    const {
        data: prefillData,
        error: prefillError,
        refetch: refetchPrefill,
    } = useGetSqPrefillPreferenceQuery({
        fetchPolicy: 'cache-first',
    });
    const {
        usePastAnswers,
        handleUsePastAnswersChange,
        error: mutationError,
    } = useSqPrefillPreference(prefillData?.autoApplyInfo?.sqPastAnswerPrefillPreference);

    const [currentPage, setCurrentPage] = useState(1);
    const [searchParams] = useSearchParams();

    const shouldIncludeHostedSQ = useShouldIncludeHostedSQ();

    // Fetch first page of past answers
    const {
        data,
        loading,
        fetchMore,
        networkStatus,
        error: pastAnswersError,
        refetch: refetchPastAnswers,
    } = useGetAutoApplyEligiblePastAnswersQuery({
        variables: { first: ITEMS_PER_PAGE, shouldIncludeHostedSQ },
        notifyOnNetworkStatusChange: true,
        // SafeMarkup (DOMPurify) used to render question text cannot run server-side.
        ssr: false,
    });

    // Show error dialog on query or mutation failure
    useEffect(() => {
        if (pastAnswersError || prefillError || mutationError) {
            setRetryAction(() => () => {
                if (pastAnswersError) void refetchPastAnswers();
                if (prefillError) void refetchPrefill();
                if (mutationError) void handleUsePastAnswersChange(!usePastAnswers);
            });
            setShowErrorDialog(true);
        }
    }, [
        pastAnswersError,
        prefillError,
        mutationError,
        refetchPastAnswers,
        refetchPrefill,
        handleUsePastAnswersChange,
        usePastAnswers,
    ]);

    const fetching = loading || networkStatus === NetworkStatus.fetchMore;

    const connection = data?.autoApplyEligiblePastAnswers;

    // Handle page change: update current page and fetch more data if needed.
    // Fetches all items up to the target page in a single request so that
    // jumping to distant pages (e.g. page 1 → page 10) works correctly.
    // Edges accumulate via the cache merge policy in client.ts.
    const onPageChange = useCallback(
        (page: number) => {
            setCurrentPage(page);

            if (!connection) return;
            const itemsNeeded = page * ITEMS_PER_PAGE;
            if (
                itemsNeeded > connection.edges.length &&
                connection.pageInfo.hasNextPage &&
                connection.pageInfo.endCursor
            ) {
                const fetchCount = itemsNeeded - connection.edges.length;
                fetchMore({
                    variables: {
                        first: fetchCount,
                        after: connection.pageInfo.endCursor,
                        shouldIncludeHostedSQ,
                    },
                }).catch(() => {
                    setCurrentPage(currentPage); // revert to previous page
                    setRetryAction(() => () => onPageChange(page));
                    setShowErrorDialog(true);
                });
            }
        },
        [connection, fetchMore, currentPage, shouldIncludeHostedSQ],
    );

    // Show current page data from accumulated edges, or undefined (spinner) if not yet available.
    // Use cached edges whenever the requested page is already fetched, even during a fetchMore
    // for a later page, to avoid flashing a spinner on already-cached pages.
    // When edges is empty (totalCount 0), pass [] so PastAnswersContent shows the empty state.
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const pageData =
        connection && (connection.edges.length === 0 || startIndex < connection.edges.length)
            ? connection.edges.slice(startIndex, startIndex + ITEMS_PER_PAGE).map((e) => e.node)
            : undefined;

    const handleClose = () => {
        if (searchParams.get('from') === 'auto-apply-setup') {
            window.close();
        } else {
            window.location.href = returnUrl;
        }
    };

    return (
        <>
            <Helmet>
                <title>{copy.pastAnswersTitle}</title>
                <meta
                    name="description"
                    content={gt.gettext('Manage your past answers for automatic applications')}
                />
            </Helmet>

            {/* Outer wrapper for centering (like AutoApplySetupLayout) */}
            <Flex
                sx={{
                    inlineSize: '100%',
                    justifyContent: 'center',
                }}
            >
                {/* Inner container with max-width */}
                <Box
                    sx={{
                        inlineSize: '100%',
                        maxInlineSize: '25.125rem', // 402px - mobile-first design
                    }}
                >
                    <Box sx={{ paddingInline: 4, paddingBlock: 2 }}>
                        <StandalonePageHeader
                            title={gt.gettext('Autofill preferences')}
                            onClose={handleClose}
                        />
                    </Box>

                    {/* Content - with bottom padding for fixed pagination */}
                    <Box
                        sx={{
                            paddingInline: 4,
                            paddingBlockEnd: 9, // Space for pagination
                        }}
                    >
                        <PastAnswersContent
                            pastAnswers={pageData}
                            usePastAnswers={usePastAnswers}
                            onUsePastAnswersChange={handleUsePastAnswersChange}
                            locale={locale}
                        />
                    </Box>
                </Box>
            </Flex>

            {/* Pagination - fixed at bottom of viewport, hidden when toggle is off */}
            {usePastAnswers && typeof connection?.totalCount === 'number' && (
                <Box
                    sx={{
                        position: 'fixed',
                        insetBlockEnd: 0,
                        insetInlineStart: '50%',
                        transform: 'translateX(-50%)',
                        inlineSize: '100%',
                        maxInlineSize: '25.125rem',
                        paddingBlock: 4,
                        backgroundColor: 'background',
                    }}
                >
                    <PaginationControls
                        currentPage={currentPage}
                        totalCount={connection.totalCount}
                        onPageChange={onPageChange}
                        itemsPerPage={ITEMS_PER_PAGE}
                        disabled={fetching}
                    />
                </Box>
            )}

            <ErrorDialog
                isOpen={showErrorDialog}
                onOpenChange={setShowErrorDialog}
                onAction={retryAction}
            />
        </>
    );
};

export default PastAnswersPage;
